// [LLDBG] TEMP diagnostic sink — remove with the rest of the [LLDBG] instrumentation.
// Routes the [LLDBG] probes to Sentry (as info-level captureMessage) AND the
// console, so an untethered on-device interview records the data in the Sentry
// dashboard without a Mac / Web Inspector attached. Matches src/main.tsx's
// `import * as Sentry from "@sentry/react"` style. Never throws into the flow.
//
// ALSO (added for the on-screen readout): every probe is pushed into a small
// in-memory ring buffer with a subscribe/getSnapshot API. LldbgOverlay reads it
// to render the values directly in the interview review UI — Sentry ingestion is
// a known dead end on-device and the Safari inspector drops mid-session, so the
// on-screen panel is the only reliable way to screenshot these values. The
// console + Sentry fan-out below is unchanged; the buffer is purely additive.
import * as Sentry from "@sentry/react";

export type LldbgEvent = {
  seq: number;
  tag: string;
  data: Record<string, unknown>;
};

// Bounded so the interview never accumulates unbounded memory. Raised to 80 as
// the prompt-outcome + playback-progress probes roughly doubled the event volume
// per question — enough to see several full prompt/record/playback cycles at once.
const RING_SIZE = 80;

let ring: LldbgEvent[] = [];
let seq = 0;
const listeners = new Set<() => void>();

// Reassigns `ring` to a NEW array on every event, so getSnapshot returns a
// referentially-stable value between events (required by useSyncExternalStore).
function push(tag: string, data: Record<string, unknown>) {
  seq += 1;
  const next = ring.length >= RING_SIZE ? ring.slice(ring.length - RING_SIZE + 1) : ring.slice();
  next.push({ seq, tag, data });
  ring = next;
  listeners.forEach((l) => {
    try {
      l();
    } catch {
      /* a bad listener must not break the diagnostic sink */
    }
  });
}

export function subscribeLldbg(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getLldbgSnapshot(): LldbgEvent[] {
  return ring;
}

export function lldbg(tag: string, data?: Record<string, unknown>) {
  const payload = data ?? {};
  try {
    console.log("[LLDBG] " + tag, payload); // keep console too
    Sentry.captureMessage("[LLDBG] " + tag, {
      level: "info",
      extra: payload,
    });
  } catch {
    /* never let diagnostics break the flow */
  }
  try {
    push(tag, payload); // feed the on-screen readout
  } catch {
    /* never let the buffer break the flow */
  }
}
